/**
 * Sanitize a filename to be safe for storage URLs.
 * - Removes diacritics (tildes, accents) using NFD normalization
 * - Replaces spaces with underscores
 * - Strips characters outside [a-zA-Z0-9._-]
 * - Collapses repeated underscores
 * - Preserves the file extension
 *
 * Example: "Práctica Final (v2).pdf" → "Practica_Final_v2.pdf"
 */
export function sanitizeFilename(filename: string): string {
  if (!filename) return 'archivo';

  // Split name and extension to preserve the extension
  const lastDot = filename.lastIndexOf('.');
  const hasExt = lastDot > 0 && lastDot < filename.length - 1;
  const baseName = hasExt ? filename.slice(0, lastDot) : filename;
  const ext = hasExt ? filename.slice(lastDot + 1) : '';

  const cleanPart = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip diacritics
      .replace(/\s+/g, '_') // spaces -> underscore
      .replace(/[^a-zA-Z0-9._-]/g, '') // strip special chars
      .replace(/_+/g, '_') // collapse underscores
      .replace(/^[_.-]+|[_.-]+$/g, ''); // trim leading/trailing separators

  const cleanBase = cleanPart(baseName) || 'archivo';
  const cleanExt = ext ? cleanPart(ext).toLowerCase() : '';

  return cleanExt ? `${cleanBase}.${cleanExt}` : cleanBase;
}

/**
 * Build a safe storage path: `${prefix}/${timestamp}_${rand}_${sanitized}`
 * El componente aleatorio evita colisiones entre subidas simultáneas del mismo nombre.
 */
export function buildSafeStoragePath(prefix: string, originalFilename: string): string {
  const safe = sanitizeFilename(originalFilename);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}/${Date.now()}_${rand}_${safe}`;
}

/** Límite de tamaño por defecto para subidas desde el cliente (25 MB). */
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

// Tipos/extensiones que NUNCA deben servirse desde un bucket público: se ejecutarían
// como HTML/JS bajo el dominio de storage (XSS). Se bloquean siempre (A14).
const BLOCKED_MIME = /^(text\/html|application\/xhtml|image\/svg)/i;
const BLOCKED_EXT = /\.(html?|xhtml|svg|js|mjs)$/i;

/**
 * Valida tamaño y tipo de un archivo ANTES de subirlo (A14). Es una capa de defensa
 * en el cliente; el bloqueo definitivo (no evadible) se hace a nivel de bucket con
 * allowed_mime_types / file_size_limit en Supabase Storage.
 */
export function validateUpload(
  file: File,
  opts?: { maxBytes?: number; allow?: RegExp; allowHtml?: boolean },
): { ok: true; error?: undefined } | { ok: false; error: string } {
  const maxBytes = opts?.maxBytes ?? UPLOAD_MAX_BYTES;
  if (file.size > maxBytes) {
    return { ok: false, error: `El archivo supera el límite de ${Math.round(maxBytes / 1024 / 1024)} MB.` };
  }
  // Excepción explícita (A14): recursos interactivos OVA en .html/.htm subidos por admins.
  // SVG y JS siguen bloqueados siempre.
  const isHtmlFile = /\.html?$/i.test(file.name) && (!file.type || /^text\/html/i.test(file.type));
  if (opts?.allowHtml && isHtmlFile) return { ok: true };
  if (BLOCKED_MIME.test(file.type) || BLOCKED_EXT.test(file.name)) {
    return { ok: false, error: 'Tipo de archivo no permitido por seguridad (HTML/SVG/JS).' };
  }
  if (opts?.allow && file.type && !opts.allow.test(file.type)) {
    return { ok: false, error: 'Tipo de archivo no permitido para este campo.' };
  }
  return { ok: true };
}

/** Content-type seguro: nunca devuelve un tipo ejecutable (fuerza octet-stream).
 *  Con `allowHtml`, los .html/.htm conservan `text/html` (recursos interactivos OVA). */
export function safeContentType(file: File, allowHtml = false): string {
  if (allowHtml && /\.html?$/i.test(file.name)) return 'text/html; charset=utf-8';
  if (!file.type || BLOCKED_MIME.test(file.type)) return 'application/octet-stream';
  return file.type;
}
